import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import clsx from 'clsx';

import { api } from '../api';
import { invalidateEventQueries, optimisticUpdateEvent } from '../event-cache';
import { eventStartDay } from '../event-day';
import type { Me } from '../App';
import type {
  Account,
  Calendar,
  CalendarEvent,
  CountdownMode,
  DailyNotesList,
  DayPulse,
  Preferences,
  View
} from '../types';
import Sidebar from '../components/Sidebar';
import MonthView from '../components/MonthView';
import WeekView from '../components/WeekView';
import DayView from '../components/DayView';
import EventPanel from '../components/EventPanel';
import DailyNoteEditor from '../components/DailyNoteEditor';
import EventCreatePopover from '../components/EventCreatePopover';
import CalendarAgenda from '../components/CalendarAgenda';
import CalendarColorPicker from '../components/CalendarColorPicker';
import HotkeysHelp from '../components/HotkeysHelp';
import Navigator from '../components/Navigator';
import QuickAdd from '../components/QuickAdd';
import ContextMenu, { type ContextMenuItem } from '../components/ContextMenu';
import ConfirmDialog from '../components/ConfirmDialog';
import MutationErrorToast from '../components/MutationErrorToast';
import SyncStatusIndicator from '../components/SyncStatusIndicator';
import WeatherChip from '../components/WeatherChip';
import WeatherPopover from '../components/WeatherPopover';
import { groupHoursByDay, useWeather, useWeatherPrefs, type WeatherView } from '../weather';
import type { AnchorRect } from '../components/Popover';

type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

type SelectedEvent = { id: string; anchor: AnchorRect };
type NewEvent = { start: Date; end: Date; allDay: boolean; anchor: AnchorRect };

function defaultsForDate(d: Date, view: View): { start: Date; end: Date; allDay: boolean } {
  // Month view: pick the next round hour (or noon for non-today). Week/day:
  // use the next hour from "now" if it's today, else 9 AM.
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (view === 'month') {
    const start = new Date(d);
    start.setHours(12, 0, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    return { start, end, allDay: false };
  }
  const start = new Date(d);
  if (isToday) {
    start.setHours(now.getHours() + 1, 0, 0, 0);
  } else {
    start.setHours(9, 0, 0, 0);
  }
  const end = new Date(start);
  end.setHours(end.getHours() + 1);
  return { start, end, allDay: false };
}

function windowFor(view: View, anchor: Date, weekStartsOn: WeekStartsOn) {
  if (view === 'day') {
    const f = new Date(anchor);
    f.setHours(0, 0, 0, 0);
    const t = new Date(f);
    t.setDate(t.getDate() + 1);
    return { from: f, to: t };
  }
  if (view === 'week') {
    return {
      from: startOfWeek(anchor, { weekStartsOn }),
      to: endOfWeek(anchor, { weekStartsOn })
    };
  }
  return {
    from: startOfWeek(startOfMonth(anchor), { weekStartsOn }),
    to: endOfWeek(endOfMonth(anchor), { weekStartsOn })
  };
}

export default function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<SelectedEvent | null>(null);
  const [newEvent, setNewEvent] = useState<NewEvent | null>(null);
  const [dailyNote, setDailyNote] = useState<{ date: string } | null>(null);
  const [agendaCalendarId, setAgendaCalendarId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [navigatorMode, setNavigatorMode] = useState<'month' | 'week' | null>(null);
  const [colorPicker, setColorPicker] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [calendarMenu, setCalendarMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [eventMenu, setEventMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [confirmDeleteEvent, setConfirmDeleteEvent] = useState<string | null>(null);
  const [dayPulse, setDayPulse] = useState<DayPulse | null>(null);
  const [weatherPop, setWeatherPop] = useState<{ date: string; anchor: AnchorRect } | null>(null);

  function openEvent(id: string, rect: DOMRect) {
    setNewEvent(null);
    setSelected({ id, anchor: rect });
  }
  function openCreate(d: Date, rect: DOMRect) {
    setSelected(null);
    const def = defaultsForDate(d, view);
    setNewEvent({ ...def, anchor: rect });
  }
  function openWeather(date: string, rect: DOMRect) {
    setSelected(null);
    setNewEvent(null);
    setWeatherPop({ date, anchor: rect });
  }
  function openDailyNote(date: string, _rect: DOMRect) {
    setSelected(null);
    setNewEvent(null);
    setDailyNote({ date });
  }

  const view: View = (params.get('view') as View) || 'month';
  const dateParam = params.get('date');
  const anchor = dateParam ? parseISO(dateParam) : new Date();

  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences')
  });
  const weekStartsOn = (prefsQ.data?.week_starts_on ?? 0) as WeekStartsOn;
  const defaultCalendarId = prefsQ.data?.default_calendar_id ?? null;
  const { from, to } = windowFor(view, anchor, weekStartsOn);

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me')
  });
  const pushConfigured = !!meQ.data?.push_configured;
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts')
  });
  const calsQ = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.get<Calendar[]>('/api/calendars')
  });
  const eventsQ = useQuery({
    queryKey: ['events', from.toISOString(), to.toISOString()],
    queryFn: () =>
      api.get<CalendarEvent[]>(`/api/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true
  });

  const countdownsQ = useQuery({
    queryKey: ['countdowns'],
    queryFn: () => api.get<CalendarEvent[]>('/api/countdowns'),
    // The badges show time remaining, so a countdown that lapses while the
    // tab sits open should drop off without a manual refresh.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true
  });

  const fromKey = format(from, 'yyyy-MM-dd');
  const toKey = format(to, 'yyyy-MM-dd');
  const dailyNotesQ = useQuery({
    queryKey: ['daily-notes', fromKey, toKey],
    queryFn: () =>
      api.get<DailyNotesList>(`/api/daily-notes?from=${fromKey}&to=${toKey}`),
    // Notes can appear/vanish out-of-band (Obsidian / LiveSync), with no in-app
    // signal — poll like the events list so deletions stop showing on their own.
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnWindowFocus: true
  });
  const noteDates = useMemo(
    () => new Set(dailyNotesQ.data?.dates ?? []),
    [dailyNotesQ.data]
  );
  const todayKey = dailyNotesQ.data?.today ?? null;

  const weatherPrefsQ = useWeatherPrefs();
  const weatherQ = useWeather(fromKey, toKey);
  const weather = useMemo<WeatherView | null>(() => {
    if (!weatherPrefsQ.data || !weatherQ.data?.location) return null;
    return {
      prefs: weatherPrefsQ.data,
      data: weatherQ.data,
      hoursByDay: groupHoursByDay(weatherQ.data.hourly)
    };
  }, [weatherPrefsQ.data, weatherQ.data]);
  const overlays = new Set(weather?.prefs.overlays ?? []);

  const setColor = useMutation({
    mutationFn: ({ id, hex }: { id: string; hex: string }) =>
      api.patch(`/api/calendars/${id}`, { background_color: hex }),
    onMutate: ({ id, hex }) => {
      qc.setQueryData<Calendar[]>(['calendars'], (old) =>
        old ? old.map((c) => (c.id === id ? { ...c, background_color: hex } : c)) : old
      );
      qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
        old
          ? old.map((ev) => (ev.calendar_id === id ? { ...ev, background_color: hex } : ev))
          : old
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const setDefaultCalendar = useMutation({
    mutationFn: (id: string | null) =>
      api.patch<Preferences>('/api/preferences', { default_calendar_id: id }),
    onMutate: (id) => {
      qc.setQueryData<Preferences>(['preferences'], (old) =>
        old ? { ...old, default_calendar_id: id } : old
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['preferences'] })
  });

  const toggleCal = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      api.patch(`/api/calendars/${id}`, { visible }),
    onMutate: ({ id, visible }) => {
      qc.setQueryData<Calendar[]>(['calendars'], (old) =>
        old ? old.map((c) => (c.id === id ? { ...c, visible } : c)) : old
      );
      if (!visible) {
        // Hiding: drop this calendar's events from the cache straight away.
        // Showing: rely on the refetch in onSettled to pull them back in.
        qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
          old ? old.filter((ev) => ev.calendar_id !== id) : old
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const setInSidebar = useMutation({
    mutationFn: ({ id, in_sidebar }: { id: string; in_sidebar: boolean }) =>
      api.patch(`/api/calendars/${id}`, { in_sidebar }),
    onMutate: ({ id, in_sidebar }) => {
      qc.setQueryData<Calendar[]>(['calendars'], (old) =>
        old ? old.map((c) => (c.id === id ? { ...c, in_sidebar } : c)) : old
      );
      if (!in_sidebar) {
        qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
          old ? old.filter((ev) => ev.calendar_id !== id) : old
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  const deleteEvent = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${id}?scope=this`),
    onSettled: () => invalidateEventQueries(qc)
  });

  const setCountdown = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: CountdownMode | null }) =>
      api.put(`/api/events/${id}/countdown`, { mode }),
    onMutate: ({ id, mode }) => {
      optimisticUpdateEvent(qc, id, { countdown: mode });
    },
    onSettled: () => {
      invalidateEventQueries(qc);
      qc.invalidateQueries({ queryKey: ['countdowns'] });
    }
  });

  type MovePatch = { id: string; start_time: string; end_time: string; all_day: boolean };
  const moveEvent = useMutation({
    mutationFn: ({ id, start_time, end_time, all_day }: MovePatch) =>
      api.patch(`/api/events/${id}`, { scope: 'this', start_time, end_time, all_day }),
    onMutate: ({ id, start_time, end_time, all_day }) => {
      optimisticUpdateEvent(qc, id, { start_time, end_time, all_day });
    },
    onSettled: () => invalidateEventQueries(qc)
  });

  const writableCalendarIds = new Set(
    (calsQ.data ?? [])
      .filter((c) => c.access_role === 'owner' || c.access_role === 'writer')
      .map((c) => c.id)
  );
  function canMove(e: CalendarEvent): boolean {
    return writableCalendarIds.has(e.calendar_id);
  }
  function onMoveEvent(e: CalendarEvent, patch: { start_time: string; end_time: string; all_day: boolean }) {
    if (!canMove(e)) return;
    moveEvent.mutate({ id: e.id, ...patch });
  }

  const sync = useMutation({
    mutationKey: ['sync'],
    mutationFn: () => api.post<{ ok: boolean; synced: number; errors: { account_id: string; message: string }[] }>('/api/sync'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['calendars'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    }
  });

  // A repeating event can be counted down two ways, so it gets both entries
  // while it's off and a single "stop" once either is set.
  function countdownMenuItems(
    ev: CalendarEvent | undefined,
    id: string
  ): ContextMenuItem[] {
    if (ev?.countdown) {
      return [
        {
          label: 'Stop counting down',
          hint: ev.countdown === 'series' ? 'every occurrence' : undefined,
          onClick: () => setCountdown.mutate({ id, mode: null })
        }
      ];
    }
    if (!ev?.is_recurring_instance) {
      return [
        { label: 'Count down to this', onClick: () => setCountdown.mutate({ id, mode: 'once' }) }
      ];
    }
    return [
      {
        label: 'Count down to this occurrence',
        onClick: () => setCountdown.mutate({ id, mode: 'once' })
      },
      {
        label: 'Count down to each occurrence',
        onClick: () => setCountdown.mutate({ id, mode: 'series' })
      }
    ];
  }

  function navigate(opts: Partial<{ view: View; date: Date }>) {
    const next = new URLSearchParams(params);
    if (opts.view) next.set('view', opts.view);
    if (opts.date) next.set('date', opts.date.toISOString());
    setParams(next, { replace: false });
  }

  function shift(direction: 1 | -1) {
    let next = anchor;
    if (view === 'day') next = addDays(anchor, direction);
    else if (view === 'week') next = addWeeks(anchor, direction);
    else next = addMonths(anchor, direction);
    navigate({ date: next });
  }

  // Flash a day cell so the eye lands on it after a jump. Bumping the key
  // replays the animation even when the same day is picked twice.
  function pulseDay(d: Date) {
    setDayPulse((cur) => ({ day: d, key: (cur?.key ?? 0) + 1 }));
  }

  function goToday() {
    const now = new Date();
    navigate({ date: now });
    pulseDay(now);
  }

  useEffect(() => {
    const popoverOpen =
      selected !== null ||
      newEvent !== null ||
      dailyNote !== null ||
      agendaCalendarId !== null ||
      helpOpen ||
      quickAddOpen ||
      navigatorMode !== null ||
      weatherPop !== null;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) {
        return;
      }
      // `?` is the one shortcut allowed through while a popup is open — it's
      // an escape hatch so the user can always summon help.
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (popoverOpen) return;
      if (e.key === ' ' && !e.shiftKey) {
        // Space on a focused button/link is "activate" — let the browser
        // handle it instead of hijacking it for QuickAdd.
        if (tag === 'BUTTON' || tag === 'A') return;
        e.preventDefault();
        setQuickAddOpen(true);
        return;
      }
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      let dir: 1 | -1 | 0 = 0;
      if (key === 'arrowright' || key === 'j') dir = 1;
      else if (key === 'arrowleft' || key === 'k') dir = -1;
      if (dir !== 0) {
        e.preventDefault();
        shift(dir);
        return;
      }
      if (key === 't') {
        e.preventDefault();
        goToday();
      } else if (key === 'm') {
        e.preventDefault();
        if (view === 'month') setNavigatorMode('month');
        else navigate({ view: 'month' });
      } else if (key === 'w') {
        e.preventDefault();
        if (view === 'week') setNavigatorMode('week');
        else navigate({ view: 'week' });
      } else if (key === 'd') {
        e.preventDefault();
        navigate({ view: 'day' });
      } else if (key === 'r') {
        e.preventDefault();
        sync.mutate();
      } else if (key === 'n') {
        e.preventDefault();
        // Today's daily note. `today` comes from the notes list payload, which
        // is keyed to the vault's timezone — prefer it over a client-side date.
        if (todayKey) {
          setSelected(null);
          setNewEvent(null);
          setDailyNote({ date: todayKey });
        }
      } else if (key === 'p') {
        e.preventDefault();
        nav('/plan');
      } else if (key === ',') {
        e.preventDefault();
        nav('/settings');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    selected,
    newEvent,
    dailyNote,
    weatherPop,
    agendaCalendarId,
    helpOpen,
    quickAddOpen,
    navigatorMode,
    view,
    anchor,
    sync,
    nav,
    todayKey
  ]);

  const heading = view === 'day' ? format(anchor, 'MMMM d, yyyy') : format(anchor, 'MMMM yyyy');

  return (
    <div className="flex h-dvh w-dvw overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <Sidebar
        anchor={anchor}
        weekStartsOn={weekStartsOn}
        accounts={accountsQ.data ?? []}
        calendars={calsQ.data ?? []}
        countdowns={countdownsQ.data ?? []}
        pushConfigured={pushConfigured}
        selectedCalendarId={agendaCalendarId}
        onPickDate={(d) => navigate({ date: d })}
        onPickCountdown={(e) => {
          const day = eventStartDay(e);
          if (!day) return;
          navigate({ date: day });
          pulseDay(day);
        }}
        onRemoveCountdown={(id) => setCountdown.mutate({ id, mode: null })}
        onPickCalendar={(id) => setAgendaCalendarId((cur) => (cur === id ? null : id))}
        onToggleCalendar={(id, visible) => toggleCal.mutate({ id, visible })}
        onPickCalendarColor={(id, rect) => setColorPicker({ id, rect })}
        onAddAccount={() => {
          window.location.href = '/google/connect';
        }}
        onOpenSettings={() => nav('/settings')}
        onOpenPlan={() => nav('/plan')}
        onCalendarContextMenu={(id, x, y) => setCalendarMenu({ id, x, y })}
      />

      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-[52px] flex-shrink-0 items-center justify-between border-b border-[var(--border)] px-4">
          <h1 className="m-0 text-base font-semibold">{heading}</h1>
          <div className="flex items-center gap-2">
            {weather && overlays.has('header_chip') && (
              <WeatherChip
                weather={weather}
                onOpen={(rect) => openWeather(format(new Date(), 'yyyy-MM-dd'), rect)}
              />
            )}
            <div className="inline-flex rounded-md bg-[var(--bg-muted)] p-[2px]">
              {(['month', 'week', 'day'] as View[]).map((v) => (
                <button
                  key={v}
                  onClick={() => navigate({ view: v })}
                  className={clsx(
                    'rounded px-3 py-[6px] text-[0.8rem] capitalize',
                    view === v
                      ? 'bg-[var(--bg)] text-[var(--text)] shadow-sm'
                      : 'text-[var(--text-muted)]'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
            <button
              onClick={goToday}
              className="rounded-md px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
            >
              Today
            </button>
            <div className="inline-flex rounded-md border border-[var(--border)]">
              <button
                aria-label="previous"
                onClick={() => shift(-1)}
                className="h-7 w-7 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
              >
                ‹
              </button>
              <button
                aria-label="next"
                onClick={() => shift(1)}
                className="h-7 w-7 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
              >
                ›
              </button>
            </div>
            <button
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              aria-label="sync"
              title="Force sync"
              className="rounded-md px-3 py-[6px] text-[0.9rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] disabled:cursor-wait disabled:opacity-50"
            >
              {sync.isPending ? '…' : '↻'}
            </button>
          </div>
        </header>

        {view === 'month' && (
          <MonthView
            anchor={anchor}
            events={eventsQ.data ?? []}
            weekStartsOn={weekStartsOn}
            dayPulse={dayPulse}
            onPickEvent={openEvent}
            onCreateAt={openCreate}
            onEventContextMenu={(id, x, y) => setEventMenu({ id, x, y })}
            canMoveEvent={canMove}
            onMoveEvent={onMoveEvent}
            noteDates={noteDates}
            todayKey={todayKey}
            onOpenDailyNote={openDailyNote}
            weather={
              overlays.has('month') ||
              overlays.has('month_temp') ||
              overlays.has('month_precip') ||
              overlays.has('month_sky')
                ? weather
                : null
            }
            onOpenWeather={openWeather}
          />
        )}
        {view === 'week' && (
          <WeekView
            anchor={anchor}
            events={eventsQ.data ?? []}
            weekStartsOn={weekStartsOn}
            dayPulse={dayPulse}
            onPickEvent={openEvent}
            onCreateAt={openCreate}
            onEventContextMenu={(id, x, y) => setEventMenu({ id, x, y })}
            canMoveEvent={canMove}
            onMoveEvent={onMoveEvent}
            noteDates={noteDates}
            todayKey={todayKey}
            onOpenDailyNote={openDailyNote}
            weather={overlays.has('week') || overlays.has('week_graph') ? weather : null}
            onOpenWeather={openWeather}
          />
        )}
        {view === 'day' && (
          <DayView
            anchor={anchor}
            events={eventsQ.data ?? []}
            dayPulse={dayPulse}
            onPickEvent={openEvent}
            onCreateAt={openCreate}
            onEventContextMenu={(id, x, y) => setEventMenu({ id, x, y })}
            noteDates={noteDates}
            todayKey={todayKey}
            onOpenDailyNote={openDailyNote}
            weather={overlays.has('day') ? weather : null}
            onOpenWeather={openWeather}
          />
        )}
      </main>
      {agendaCalendarId && (() => {
        const cal = (calsQ.data ?? []).find((c) => c.id === agendaCalendarId);
        if (!cal) return null;
        return (
          <CalendarAgenda
            calendar={cal}
            onClose={() => setAgendaCalendarId(null)}
            onPickEvent={openEvent}
          />
        );
      })()}
      {selected && (
        <EventPanel
          eventId={selected.id}
          anchor={selected.anchor}
          onClose={() => setSelected(null)}
        />
      )}
      {dailyNote && (
        <DailyNoteEditor
          date={dailyNote.date}
          onClose={() => setDailyNote(null)}
        />
      )}
      {weatherPop && weather && (
        <WeatherPopover
          date={weatherPop.date}
          isToday={weatherPop.date === format(new Date(), 'yyyy-MM-dd')}
          anchor={weatherPop.anchor}
          weather={weather}
          onClose={() => setWeatherPop(null)}
        />
      )}
      <SyncStatusIndicator />
      <MutationErrorToast />
      <HotkeysHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <QuickAdd
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        calendars={calsQ.data ?? []}
      />
      {colorPicker && (() => {
        const cal = (calsQ.data ?? []).find((c) => c.id === colorPicker.id);
        if (!cal) return null;
        return (
          <CalendarColorPicker
            anchorRect={colorPicker.rect}
            currentHex={cal.background_color}
            onPick={(hex) => {
              setColor.mutate({ id: colorPicker.id, hex });
              setColorPicker(null);
            }}
            onClose={() => setColorPicker(null)}
          />
        );
      })()}
      {navigatorMode && (
        <Navigator
          mode={navigatorMode}
          anchor={anchor}
          weekStartsOn={weekStartsOn}
          onPick={(d) => {
            setNavigatorMode(null);
            navigate({ date: d });
          }}
          onClose={() => setNavigatorMode(null)}
        />
      )}
      {newEvent && (
        <EventCreatePopover
          anchor={newEvent.anchor}
          calendars={calsQ.data ?? []}
          initialStart={newEvent.start}
          initialEnd={newEvent.end}
          initialAllDay={newEvent.allDay}
          onClose={() => setNewEvent(null)}
        />
      )}
      {eventMenu && (() => {
        const ev = (eventsQ.data ?? []).find((e) => e.id === eventMenu.id);
        const items: ContextMenuItem[] = [
          {
            label: 'Open',
            onClick: () => {
              const rect = new DOMRect(eventMenu.x, eventMenu.y, 0, 0);
              openEvent(eventMenu.id, rect);
            }
          },
          ...countdownMenuItems(ev, eventMenu.id),
          { divider: true },
          {
            label: 'Delete event…',
            danger: true,
            onClick: () => setConfirmDeleteEvent(eventMenu.id)
          }
        ];
        return (
          <ContextMenu
            open={true}
            x={eventMenu.x}
            y={eventMenu.y}
            items={items}
            onClose={() => setEventMenu(null)}
            ariaLabel={ev?.summary ? `actions for ${ev.summary}` : 'event actions'}
          />
        );
      })()}
      <ConfirmDialog
        open={confirmDeleteEvent !== null}
        title="Delete event?"
        message="This deletes this occurrence on Google Calendar. Recurring series and other instances are unaffected."
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (confirmDeleteEvent) deleteEvent.mutate(confirmDeleteEvent);
          setConfirmDeleteEvent(null);
        }}
        onClose={() => setConfirmDeleteEvent(null)}
      />
      {calendarMenu && (() => {
        const cal = (calsQ.data ?? []).find((c) => c.id === calendarMenu.id);
        if (!cal) return null;
        const isDefault = defaultCalendarId === cal.id;
        const writable = cal.access_role === 'owner' || cal.access_role === 'writer';
        const items: ContextMenuItem[] = [
          {
            label: 'Open agenda',
            onClick: () => setAgendaCalendarId(cal.id)
          },
          {
            label: 'Change color…',
            onClick: () => {
              // Anchor to the cursor location since the click was on a row.
              const rect = new DOMRect(calendarMenu.x, calendarMenu.y, 0, 0);
              setColorPicker({ id: cal.id, rect });
            }
          },
          { divider: true },
          isDefault
            ? {
                label: 'Unset as default calendar',
                onClick: () => setDefaultCalendar.mutate(null)
              }
            : {
                label: 'Set as default calendar',
                disabled: !writable,
                hint: !writable ? 'read-only' : undefined,
                onClick: () => setDefaultCalendar.mutate(cal.id)
              },
          {
            label: cal.visible ? 'Hide events' : 'Show events',
            onClick: () => toggleCal.mutate({ id: cal.id, visible: !cal.visible })
          },
          {
            label: 'Remove from sidebar',
            onClick: () => setInSidebar.mutate({ id: cal.id, in_sidebar: false })
          },
          { divider: true },
          {
            label: 'Calendar settings…',
            onClick: () => nav('/settings')
          }
        ];
        return (
          <ContextMenu
            open={true}
            x={calendarMenu.x}
            y={calendarMenu.y}
            items={items}
            onClose={() => setCalendarMenu(null)}
            ariaLabel={`actions for ${cal.summary}`}
          />
        );
      })()}
    </div>
  );
}
